import { Link, useParams } from 'react-router';
import { useRecall, useRecalls, type RecallView } from '../api/recall-queries.js';
import { Badge } from '../components/badge.js';
import { EmptyState } from '../components/empty-state.js';
import { ErrorState } from '../components/error-state.js';
import { PageHeader } from '../components/page-header.js';
import { formatDateTime } from '../lib/format.js';

const STATUS: Record<string, string> = {
  PENDING: 'Recibido',
  QUARANTINING: 'Aislando productos',
  WATCHING: 'Reporte enviado, en seguimiento',
  RETRYING: 'Respuesta parcial, reintentando',
  CLOSED: 'Plazo cerrado',
  EXPIRED: 'Sin reporte dentro del plazo',
};

function RecallSummary({ recall }: { recall: RecallView }) {
  const seconds =
    recall.reportedAt === null
      ? null
      : Math.max(
          0,
          Math.round((Date.parse(recall.reportedAt) - Date.parse(recall.issuedAt)) / 1_000),
        );
  return (
    <>
      <Badge tone={recall.reportedAt === null ? 'warning' : 'success'}>
        {STATUS[recall.status] ?? recall.status}
      </Badge>
      <p className="mt-3 font-mono text-sm break-all">
        {recall.recallId} · {recall.sku}
      </p>
      <p className="mt-2">{recall.reason}</p>
      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Emitido</dt>
          <dd>{formatDateTime(recall.issuedAt)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Plazo</dt>
          <dd>{formatDateTime(recall.deadline)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Tiempo hasta el último reporte</dt>
          <dd>{seconds === null ? 'Pendiente' : `${String(seconds)} s`}</dd>
        </div>
      </dl>
      <Link
        className="mt-4 inline-block min-h-11 py-2 text-accent underline"
        to={`/trazabilidad?lote=${encodeURIComponent(recall.batch)}`}
      >
        Ver lote {recall.batch}
      </Link>
    </>
  );
}

export function RecallsPage() {
  const query = useRecalls();
  return (
    <>
      <PageHeader
        title="Retiros de lote"
        description="Avisos recibidos de Farma Central y productos retirados de circulación. Las fechas se muestran en hora de Chile."
      />
      {query.isPending && <p role="status">Cargando retiros…</p>}
      {query.isError && (
        <ErrorState
          message="No se pudieron cargar los retiros."
          onRetry={() => void query.refetch()}
        />
      )}
      {query.data?.length === 0 && <EmptyState message="No se han recibido retiros de lote." />}
      <div className="space-y-4">
        {query.data?.map((recall) => (
          <article key={recall.recallId} className="rounded-card border border-border bg-card p-6">
            <RecallSummary recall={recall} />
            <Link
              to={`/recalls/${encodeURIComponent(recall.recallId)}`}
              className="ml-4 inline-block min-h-11 py-2 font-medium text-accent underline"
            >
              Ver productos retirados
            </Link>
          </article>
        ))}
      </div>
    </>
  );
}

export function RecallDetailPage() {
  const { id = '' } = useParams();
  const query = useRecall(id);
  const recall = query.data;
  return (
    <>
      <Link to="/recalls" className="mb-4 inline-block min-h-11 py-2 text-accent underline">
        Volver a retiros
      </Link>
      <PageHeader
        title="Detalle del retiro"
        description="Productos afectados, lotes derivados y órdenes notificadas."
      />
      {query.isPending && <p role="status">Cargando retiro…</p>}
      {query.isError && (
        <ErrorState
          message="No se pudo consultar este retiro."
          onRetry={() => void query.refetch()}
        />
      )}
      {recall && (
        <div className="space-y-6">
          <section
            aria-label="Resumen del retiro"
            className="rounded-card border border-border bg-card p-6"
          >
            <RecallSummary recall={recall} />
          </section>
          <section>
            <h2 className="mb-3 text-xl font-bold">Productos retirados de circulación</h2>
            {recall.products.length === 0 ? (
              <EmptyState message="Aún no se han registrado productos en stock afectados." />
            ) : (
              <div className="overflow-x-auto rounded-card border border-border bg-card">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">
                    Identidad y estado de cada producto del retiro
                  </caption>
                  <thead className="bg-muted">
                    <tr>
                      {['SKU', 'ID del producto', 'Lote', 'Ubicación original', 'Cuarentena'].map(
                        (label) => (
                          <th key={label} scope="col" className="p-3">
                            {label}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {recall.products.map((product) => (
                      <tr key={product.productId} className="border-t border-border">
                        <td className="p-3">{product.sku}</td>
                        <td className="p-3 font-mono">{product.productId}</td>
                        <td className="p-3">
                          <Link
                            className="text-accent underline"
                            to={`/trazabilidad?lote=${encodeURIComponent(product.batch)}`}
                          >
                            {product.batch}
                          </Link>
                        </td>
                        <td className="p-3">{product.sourceSpaceId}</td>
                        <td className="p-3">
                          {product.quarantinedAt === null
                            ? 'Traslado pendiente'
                            : formatDateTime(product.quarantinedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section>
            <h2 className="mb-3 text-xl font-bold">Lotes derivados</h2>
            {recall.derivedBatches.length === 0 ? (
              <EmptyState message="No hay lotes derivados registrados." />
            ) : (
              <ul className="space-y-2">
                {recall.derivedBatches.map((lot) => (
                  <li
                    key={`${lot.batch}:${lot.sourceBatch}`}
                    className="rounded-control border border-border bg-card p-3"
                  >
                    {lot.sku} ·{' '}
                    <Link
                      className="text-accent underline"
                      to={`/trazabilidad?lote=${encodeURIComponent(lot.batch)}`}
                    >
                      {lot.batch}
                    </Link>
                    <span className="block text-sm text-muted-foreground">
                      Derivado de {lot.sourceBatch}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <h2 className="mb-3 text-xl font-bold">Órdenes notificadas</h2>
            {recall.notifications.length === 0 ? (
              <EmptyState message="No hay órdenes notificadas registradas." />
            ) : (
              <ul className="space-y-2">
                {recall.notifications.map((notification) => (
                  <li
                    key={notification.orderId}
                    className="rounded-control border border-border bg-card p-3"
                  >
                    <span className="font-mono">{notification.orderId}</span> ·{' '}
                    {notification.productIds.length} productos ·{' '}
                    {notification.notifiedAt === null
                      ? 'Notificación pendiente'
                      : formatDateTime(notification.notifiedAt)}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {recall.localOnly.length > 0 && (
            <p className="rounded-card border border-border bg-muted p-4 text-sm">
              Hay {recall.localOnly.length} ventas del portal con productos afectados. Sus unidades
              físicas se aíslan; estas ventas no tienen una orden de compra externa para notificar.
            </p>
          )}
        </div>
      )}
    </>
  );
}
